import { z } from "zod";
import {
  companyStageSchema,
  contactStatusSchema,
  emailStatusSchema,
  importKindSchema,
  importStatusSchema,
  rejectReasonSchema,
  reviewActionSchema,
  enrichmentFactsSchema,
  markdownSchema,
  scoreSchema,
  sequenceStatusSchema,
  sourceTypeSchema,
  strategyKeySchema,
  strategyStatusSchema,
  strategyTypeSchema,
  suppressionKindSchema,
  suppressionReasonSchema,
} from "./pipeline.js";
import type { ReferenceClient } from "./pipeline.js";

/**
 * The REST contract between the Worker and the SPA.
 *
 * Response shapes are declared here rather than inferred from Drizzle rows so
 * the wire format is a deliberate choice: dates are ISO strings, database
 * columns the UI has no business knowing about stay server-side, and a schema
 * change cannot silently reshape the API. Request schemas are the same objects
 * the Worker validates with, so the two sides cannot disagree about what a
 * valid patch looks like.
 */

// --- Requests ---------------------------------------------------------------

/** Query string for the pipeline table. Arrays arrive as repeated params. */
export const companyQuerySchema = z.object({
  query: z.string().max(200).optional(),
  stage: z.array(companyStageSchema).optional(),
  industry: z.string().max(120).optional(),
  size_band: z.string().max(40).optional(),
  source_type: sourceTypeSchema.optional(),
  sourcing_strategy_key: strategyKeySchema.optional(),
  min_score: z.coerce.number().int().min(0).max(100).optional(),
  has_contacts: z.enum(["true", "false"]).optional(),
  stale_enrichment: z.enum(["true", "false"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.enum(["score", "recent", "name"]).default("score"),
});
export type CompanyQuery = z.infer<typeof companyQuerySchema>;

/**
 * A human's edit to a company.
 *
 * Same field set as the agent's patch minus `run_id` — a person acts as
 * themselves, not on behalf of a run. `reason` is still mandatory when parking
 * or killing: the reason is a learning signal about the *strategy*, and it is
 * no less valuable when a human supplies it.
 */
export const updateCompanyRequestSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    industry: z.string().max(120).nullish(),
    size_band: z.string().max(40).nullish(),
    employee_count: z.number().int().min(0).max(10_000_000).nullish(),
    region: z.string().max(120).nullish(),
    score: scoreSchema.optional(),
    score_rationale_md: markdownSchema.optional(),
    stage: companyStageSchema.optional(),
    reason: z.string().min(3).max(2000).optional(),
    hubspot_id: z.string().max(120).nullish(),
    enrichment: enrichmentFactsSchema.optional(),
    research_md: markdownSchema.optional(),
  })
  .refine((p) => Object.keys(p).length > 0, "Nothing to update.");
export type UpdateCompanyRequest = z.infer<typeof updateCompanyRequestSchema>;

/** Qualify, park or kill a screenful of companies at once (docs/02 §3.2). */
export const bulkStageRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  stage: companyStageSchema,
  reason: z.string().min(3).max(2000).optional(),
});
export type BulkStageRequest = z.infer<typeof bulkStageRequestSchema>;

/**
 * Per-company outcome of a bulk action.
 *
 * Bulk stage changes report per item for the same reason the agent tools do:
 * some rows in a selection are legitimately in a stage that cannot make the
 * requested move, and failing the whole batch would leave the operator unsure
 * what did and did not happen.
 */
export interface BulkStageResult {
  id: string;
  ok: boolean;
  message: string | null;
}

// --- Responses --------------------------------------------------------------

export interface CompanySummary {
  id: string;
  name: string;
  domain: string;
  industry: string | null;
  sizeBand: string | null;
  employeeCount: number | null;
  region: string | null;
  sourceType: "cold" | "warm";
  score: number | null;
  stage: z.infer<typeof companyStageSchema>;
  sourcingRationaleMd: string | null;
  enrichedAt: string | null;
  /** True when research is missing or older than ENRICHMENT_STALE_DAYS. */
  enrichmentStale: boolean;
  lastActivityAt: string;
  createdAt: string;
  contactCount: number;
  sequenceCount: number;
}

export interface CompanyListResponse {
  companies: CompanySummary[];
  total: number;
}

export interface ContactSummary {
  id: string;
  fullName: string;
  roleTitle: string | null;
  personaKey: string | null;
  seniority: string | null;
  email: string | null;
  emailStatus: z.infer<typeof emailStatusSchema> | null;
  linkedinUrl: string | null;
  location: string | null;
  status: z.infer<typeof contactStatusSchema>;
  researchMd: string | null;
  enrichment: Record<string, unknown>;
  enrichedAt: string | null;
}

export interface SequenceSummary {
  id: string;
  contactId: string;
  status: z.infer<typeof sequenceStatusSchema>;
  variantLabel: string | null;
  draftGroup: string;
  rationaleMd: string | null;
  createdAt: string;
}

/** Which strategy version produced something — the attribution chip. */
export interface StrategyRef {
  key: string;
  name: string;
  version: number;
}

export interface TimelineEntryDto {
  id: string;
  source: "audit" | "run_event" | "comment";
  at: string;
  kind: string;
  message: string | null;
  entityType: string | null;
  entityId: string | null;
  actorType: string | null;
  actorLabel: string | null;
  runId: string | null;
  strategyKey: string | null;
  before: unknown;
  after: unknown;
}

export interface CompanyDetailResponse {
  company: {
    id: string;
    name: string;
    domain: string;
    industry: string | null;
    sizeBand: string | null;
    employeeCount: number | null;
    region: string | null;
    sourceType: "cold" | "warm";
    score: number | null;
    scoreRationaleMd: string | null;
    stage: z.infer<typeof companyStageSchema>;
    killedReason: string | null;
    hubspotId: string | null;
    sourcingRationaleMd: string | null;
    researchMd: string | null;
    enrichment: Record<string, unknown>;
    enrichedAt: string | null;
    createdAt: string;
    lastActivityAt: string;
  };
  sourcingStrategy: StrategyRef | null;
  contacts: ContactSummary[];
  sequences: SequenceSummary[];
  timeline: TimelineEntryDto[];
  enrichmentStale: boolean;
}

export interface PersonaDto {
  key: string;
  label: string;
  sizeBands: string[];
  description: string | null;
}

export interface RunSummary {
  id: string;
  strategyKey: string;
  strategyVersion: number;
  status: "running" | "succeeded" | "failed" | "abandoned";
  startedByLabel: string;
  startedAt: string;
  finishedAt: string | null;
  summaryMd: string | null;
  stats: Record<string, number> | null;
  /** Attribution: which token, whose it is, and where it called from. */
  agentTokenId: string | null;
  agentTokenName: string | null;
  agentTokenOwner: string | null;
  agentTokenRevoked: boolean;
  startedFromIp: string | null;
  stoppedBy: string | null;
  stoppedReason: string | null;
}

export const stopRunRequestSchema = z.object({
  reason: z
    .string()
    .min(3, "Say why you are stopping it — the run log keeps this.")
    .max(500),
});

/** Dashboard snapshot — the same picture agents get from `get_context`. */
export interface PipelineContextResponse {
  strategies: Record<
    string,
    { key: string; name: string; version: number; status: string }[]
  >;
  /** Company counts keyed by stage; absent stages mean zero. */
  pipeline: Record<string, number>;
  pendingWork: {
    awaitingQualification: number;
    needsContacts: number;
    needsEnrichment: number;
    awaitingReview: number;
    approvedNotSent: number;
  };
  suppression: { total: number; emails: number; domains: number };
  personas: PersonaDto[];
  settings: Record<string, unknown>;
  recentRuns: RunSummary[];
}

// --- Review queue -----------------------------------------------------------

/**
 * A reviewer's decision.
 *
 * `reasons` is required on a rejection and drawn from a fixed taxonomy, because
 * these counts are what a strategist acts on. Free text rides alongside in
 * `comment_md` for the detail the taxonomy can't carry.
 */
export const reviewDecisionSchema = z
  .object({
    action: reviewActionSchema,
    reasons: z.array(rejectReasonSchema).max(8).optional(),
    comment_md: z.string().max(4000).optional(),
    /** Reviewer edits, applied before approval and kept as feedback. */
    edits: z
      .array(
        z.object({
          email_id: z.string().uuid(),
          subject: z.string().min(1).max(300).optional(),
          body_md: z.string().min(1).max(8000).optional(),
        }),
      )
      .max(12)
      .optional(),
  })
  .refine(
    (d) => d.action !== "rejected" || (d.reasons?.length ?? 0) > 0,
    "Pick at least one reason — the counts are what tell a strategist which playbook to fix.",
  );
export type ReviewDecisionRequest = z.infer<typeof reviewDecisionSchema>;

export interface ReviewQueueItem {
  id: string;
  draftGroup: string;
  variantLabel: string | null;
  createdAt: string;
  contactName: string;
  contactRole: string | null;
  companyId: string;
  companyName: string;
  companyScore: number | null;
  strategyKey: string | null;
  emailCount: number;
}

export interface SequenceEmailDto {
  id: string;
  position: number;
  waitDays: number;
  subject: string;
  bodyMd: string;
  status: "pending" | "sent" | "skipped";
}

/** Everything the review screen shows for one sequence, in one request. */
export interface SequenceDetailResponse {
  sequence: {
    id: string;
    status: z.infer<typeof sequenceStatusSchema>;
    variantLabel: string | null;
    draftGroup: string;
    rationaleMd: string | null;
    createdAt: string;
    approvedAt: string | null;
  };
  strategy: StrategyRef | null;
  company: {
    id: string;
    name: string;
    domain: string;
    industry: string | null;
    sizeBand: string | null;
    score: number | null;
    researchMd: string | null;
    enrichment: Record<string, unknown>;
    sourcingRationaleMd: string | null;
  };
  contact: {
    id: string;
    fullName: string;
    roleTitle: string | null;
    email: string | null;
    emailStatus: z.infer<typeof emailStatusSchema> | null;
    linkedinUrl: string | null;
    researchMd: string | null;
  };
  emails: SequenceEmailDto[];
  /** Alternative drafts in the same group — picking one retires the others. */
  siblings: {
    id: string;
    variantLabel: string | null;
    status: z.infer<typeof sequenceStatusSchema>;
  }[];
}

export interface ApprovalStat {
  strategyKey: string;
  approved: number;
  rejected: number;
  total: number;
  /** Null when nothing has been reviewed — a rate without an n misleads. */
  approvalRate: number | null;
}

export interface ReviewQueueResponse {
  queue: ReviewQueueItem[];
  stats: ApprovalStat[];
}

// --- Strategies -------------------------------------------------------------

export const createStrategyRequestSchema = z.object({
  key: strategyKeySchema,
  type: strategyTypeSchema,
  name: z.string().min(1).max(120),
  playbook_md: markdownSchema.min(50, "A playbook needs actual instructions."),
});

/** Editing a playbook never mutates a version — it creates the next one. */
export const newVersionRequestSchema = z.object({
  playbook_md: markdownSchema.min(50),
  change_note: z
    .string()
    .min(1, "Say what changed and why — the next person reading the history is you.")
    .max(2000),
});

export const versionStatusRequestSchema = z.object({
  version: z.number().int().min(1),
  status: z.enum(["active", "paused", "archived", "draft"]),
});

export interface StrategyVersionDto {
  id: string;
  version: number;
  status: StrategyStatusValue;
  playbookMd: string;
  changeNote: string | null;
  createdAt: string;
  runs: number;
}

export type StrategyStatusValue = z.infer<typeof strategyStatusSchema>;

export interface StrategyListItem {
  key: string;
  name: string;
  type: z.infer<typeof strategyTypeSchema>;
  version: number;
  status: StrategyStatusValue;
  updatedAt: string;
  /** Headline number: approval rate for outreach, qualification rate otherwise. */
  headline: { label: string; value: number | null; n: number } | null;
  lastRunAt: string | null;
}

export interface ReasonCountDto {
  reason: string;
  count: number;
}

export interface StrategyMetricsDto {
  key: string;
  name: string;
  type: string;
  versions: { version: number; status: string; runs: number }[];
  runs: { total: number; succeeded: number; failed: number };
  produced: number;
  funnel: { stage: string; count: number }[];
  killReasons: ReasonCountDto[];
  review: {
    approved: number;
    rejected: number;
    total: number;
    approvalRate: number | null;
    reasons: ReasonCountDto[];
  } | null;
  outcomes: ReasonCountDto[];
}

export interface StrategyDetailResponse {
  key: string;
  name: string;
  type: z.infer<typeof strategyTypeSchema>;
  versions: StrategyVersionDto[];
  metrics: StrategyMetricsDto | null;
}

// --- Runs -------------------------------------------------------------------

export interface RunEventDto {
  id: string;
  kind: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
}

export interface RunDetailResponse {
  run: RunSummary;
  events: RunEventDto[];
  /** What this run created, so its output is one click away. */
  produced: {
    companies: { id: string; name: string; domain: string }[];
    contacts: { id: string; fullName: string; companyId: string }[];
    sequences: { id: string; status: string; contactId: string }[];
  };
}

// --- Settings ---------------------------------------------------------------

export interface SuppressionEntryDto {
  id: string;
  kind: "email" | "domain";
  value: string;
  reason: "hubspot" | "unsubscribed" | "bounced" | "manual";
  sourceRef: string | null;
  createdAt: string;
  /** False for unsubscribes and bounces — those are not ours to reverse. */
  removable: boolean;
}

export const addSuppressionRequestSchema = z.object({
  kind: suppressionKindSchema,
  value: z.string().min(3).max(320),
  reason: suppressionReasonSchema.default("manual"),
  source_ref: z.string().max(200).optional(),
});

export const updateSettingRequestSchema = z.object({
  key: z.enum([
    "icp_definition",
    "reference_clients",
    "social_proof",
    "house_rules",
  ]),
  value: z.unknown(),
});

export const upsertPersonaRequestSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9_]+$/, "Lower-case, digits and underscores only."),
  label: z.string().min(1).max(120),
  size_bands: z.array(z.string().max(40)).max(10),
  description: z.string().max(1000).nullish(),
  sort_order: z.number().int().min(0).max(1000).optional(),
  active: z.boolean().optional(),
});

export interface AgentTokenDto {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Who minted it, and where it was last used from. */
  createdByEmail: string | null;
  lastUsedIp: string | null;
  /** Runs still open under this token — what revoking it would interrupt. */
  activeRuns: number;
}

// --- Pasted imports ---------------------------------------------------------

export const createImportRequestSchema = z.object({
  kind: importKindSchema,
  raw_text: z
    .string()
    .min(2, "Paste something first.")
    .max(100_000, "That's too much for one paste — split it up."),
  source_note: z.string().max(200).optional(),
});

export interface ImportDto {
  id: string;
  kind: z.infer<typeof importKindSchema>;
  status: z.infer<typeof importStatusSchema>;
  rawText: string;
  sourceNote: string | null;
  resultMd: string | null;
  stats: Record<string, number> | null;
  runId: string | null;
  createdAt: string;
  processedAt: string | null;
  /** Line count, so the UI can summarise without rendering the whole paste. */
  lineCount: number;
}

export interface ReferenceClientsResponse {
  raw: string | null;
  clients: ReferenceClient[];
  /** Pastes not yet turned into clients. */
  pendingImports: ImportDto[];
}
